import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiStar, FiCheck, FiArrowRight, FiUser, FiX } from 'react-icons/fi';
import { PlanType } from "../../types/index";



const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const UpgradeContainer = styled.div`
  padding: 40px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #111827;
  text-align: center;
  width: 100%;
  max-width: 1300px;  /* ✅ keep proportional with modal width */
  border-radius: 24px;
  margin: 0 auto;
`;





const UpgradeIcon = styled.div`
  font-size: 54px;
  margin-bottom: 18px;
`;

const UpgradeTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const UpgradeSubtitle = styled.p`
  margin: 0 0 28px 0;
  font-size: 15px;
  opacity: 0.92;
  line-height: 1.6;
`;

const PlansContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 28px;
  margin-bottom: 28px;
  width: 100%;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 10px;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const PlanCard = styled.div<{ selected?: boolean }>`
  flex: 0 0 340px;
  max-width: 360px;
  height: 460px; /* ✅ fixed consistent height */
  display: flex;
  flex-direction: column;
  justify-content: space-between; /* ✅ keep footer button aligned */
  background: ${({ selected }) =>
    selected
      ? 'linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)'
      : 'white'};
  border-radius: 20px;
  padding: 32px 24px;
  border: 3px solid ${({ selected }) => (selected ? '#E89B3C' : 'transparent')};
  box-shadow: ${({ selected }) =>
    selected
      ? '0 8px 32px rgba(232, 155, 60, 0.25)'
      : '0 3px 12px rgba(0,0,0,0.08)'};
  cursor: pointer;
  transition: all 0.3s ease;
  scroll-snap-align: center;

  &:hover {
    transform: translateY(-3px);
    border-color: #e89b3c;
  }
`;





const SelectedBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: white;
  color: #E89B3C;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  border: 2px solid #E89B3C;
`;

const MostPopularBadge = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #E89B3C 0%, #FF9900 100%);
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 16px;
  border-radius: 20px;
  letter-spacing: 0.8px;
  box-shadow: 0 4px 16px rgba(232, 155, 60, 0.3);
  border: 2px solid white;
  text-transform: uppercase;
  animation: ${fadeIn} 0.6s ease-out;
`;

const PlanName = styled.div<{ selected?: boolean }>`
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 16px;
  color: ${({ selected }) => (selected ? 'white' : '#111827')};
`;

const PlanPrice = styled.div<{ selected?: boolean }>`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${({ selected }) => (selected ? 'white' : '#6b7280')};
`;

const PlanPeriod = styled.div<{ selected?: boolean }>`
  font-size: 13px;
  opacity: ${({ selected }) => (selected ? 0.9 : 0.8)};
  margin-bottom: 14px;
  color: ${({ selected }) => (selected ? 'white' : 'inherit')};
`;

const PlanFeatures = styled.ul<{ selected?: boolean }>`
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
  font-size: 13px;
  text-align: left;
  color: ${({ selected }) => (selected ? 'white' : '#444')};
  flex-grow: 1; /* ✅ take remaining vertical space */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 5px;
`;

const PlanFeature = styled.li<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 5px;
  opacity: ${({ selected }) => (selected ? 0.95 : 0.93)};
  color: ${({ selected }) => (selected ? 'white' : 'inherit')};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'premium' }>`
  width: 100%;
  padding: 16px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 10;
  margin-top: 4px;
  position: relative;
  overflow: hidden;
  
  ${({ variant }) => variant === 'secondary' ? `
    background: #f3f4f6;
    color: #6b7280;
    border: 2px solid #e5e7eb;
    &:hover:not(:disabled) {
      background: #e5e7eb;
      border-color: #d1d5db;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
  ` : variant === 'premium' ? `
    background: linear-gradient(135deg, #E89B3C 0%, #FF9900 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(232, 155, 60, 0.25);
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #FF9900 0%, #E89B3C 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(232, 155, 60, 0.35);
    }
  ` : `
    background: linear-gradient(135deg, #E89B3C 0%, #FF9900 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(232, 155, 60, 0.25);
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #FF9900 0%, #E89B3C 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(232, 155, 60, 0.35);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  max-width: 320px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  margin-top: 18px;
  opacity: 0.7;
  font-size: 15px;
  font-weight: 500;
  transition: opacity 0.2s, color 0.2s;
  &:hover {
    opacity: 1;
    color: #4b5563;
  }
`;

interface UpgradePromptProps {
  onUpgrade: (plan: PlanType) => void | Promise<void>;
  onClose: () => void;
  isAuthenticated?: boolean;
  loading?: boolean;
  currentPlan?: PlanType;
  limits?: {
    text_messages: number;
    voice_minutes: number;
    image_uploads: number;
  };
}
 

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ 
  onUpgrade, 
  onClose, 
  isAuthenticated = true, 
  loading = false, 
  currentPlan = 'free',
  limits 
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'free-trial' | 'basic' | 'premium'>(
    currentPlan === 'free' ? 'free-trial' : 'premium'
  );

  const handleUpgrade = () => {
    onUpgrade(selectedPlan);
  };

  const availablePlans = currentPlan === 'free'
    ? ['free-trial', 'basic', 'premium']
    : currentPlan === 'basic'
    ? ['premium']
    : [];

  const isBasicUser = currentPlan === 'basic';

  const getFreeLimit = () => limits && limits.text_messages !== -1 ? limits.text_messages : 6;
  const getBasicVoiceLimit = () => limits && limits.voice_minutes !== -1 ? limits.voice_minutes : 60;
  const getBasicImageLimit = () => limits && limits.image_uploads !== -1 ? limits.image_uploads : 10;

  return (
<UpgradeContainer>
  <UpgradeIcon>{isAuthenticated ? '⭐' : <FiUser size={54} />}</UpgradeIcon>
  <UpgradeTitle>
    {isAuthenticated 
      ? (isBasicUser ? 'Upgrade to Premium' : 'Upgrade Your Plan')
      : `Join ${process.env.REACT_APP_WIDGET_TITLE || 'K9 Coach AI'}`}
  </UpgradeTitle>
  <UpgradeSubtitle>
    {isAuthenticated 
      ? (isBasicUser 
          ? "Upgrade to Premium for more messages, voice minutes, and image uploads!"
          : "You've reached your plan limits. Upgrade to unlock more messages, voice features, and image uploads!")
      : `You've used all ${getFreeLimit()} demo messages! Create an account to continue chatting with AI-powered dog  training advice.`}
  </UpgradeSubtitle>

  <PlansContainer>
  {/* Basic */}
  <PlanCard selected={selectedPlan === 'basic'} onClick={() => setSelectedPlan('basic')} tabIndex={0}>
    {selectedPlan === 'basic' && <SelectedBadge><FiCheck /></SelectedBadge>}
    <PlanName selected={selectedPlan === 'basic'}>Basic</PlanName>
    <PlanPrice selected={selectedPlan === 'basic'}>$9.99</PlanPrice>
    <PlanPeriod selected={selectedPlan === 'basic'}>per month</PlanPeriod>
    <PlanFeatures selected={selectedPlan === 'basic'}>
      <PlanFeature selected={selectedPlan === 'basic'}>
        ✔ 1000 text messages
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'basic'}>
        ✔ 20 voice minutes
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'basic'}>
        ✔ 10 image uploads
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'basic'}>
        ✔ Voice Notes + Image Analysis
      </PlanFeature>
    </PlanFeatures>
  </PlanCard>

  {/* Premium */}
  <PlanCard selected={selectedPlan === 'premium'} onClick={() => setSelectedPlan('premium')} tabIndex={0}>
    <MostPopularBadge>Most Popular</MostPopularBadge>
    {selectedPlan === 'premium' && <SelectedBadge><FiCheck /></SelectedBadge>}
    <PlanName selected={selectedPlan === 'premium'}>Premium</PlanName>
    <PlanPrice selected={selectedPlan === 'premium'}>$19.99</PlanPrice>
    <PlanPeriod selected={selectedPlan === 'premium'}>per month</PlanPeriod>
    <PlanFeatures selected={selectedPlan === 'premium'}>
      <PlanFeature selected={selectedPlan === 'premium'}>
        ✔ 2500 text messages
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'premium'}>
        ✔ 50 voice minutes
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'premium'}>
        ✔ 15 image uploads
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'premium'}>
        ✔ Everything in Basic plan
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'premium'}>
        ✔ Pre-recorded video recommendation & guidance
      </PlanFeature>
    </PlanFeatures>
  </PlanCard>

  {/* Ultimate */}
  <PlanCard selected={selectedPlan === 'free-trial'} onClick={() => setSelectedPlan('free-trial')} tabIndex={0}>
    {selectedPlan === 'free-trial' && <SelectedBadge><FiCheck /></SelectedBadge>}
    <PlanName selected={selectedPlan === 'free-trial'}>Ultimate</PlanName>
    <PlanPrice selected={selectedPlan === 'free-trial'}>$29.99</PlanPrice>
    <PlanPeriod selected={selectedPlan === 'free-trial'}>per month</PlanPeriod>
    <PlanFeatures selected={selectedPlan === 'free-trial'}>
      <PlanFeature selected={selectedPlan === 'free-trial'}>
        ✔ 4000 text messages
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'free-trial'}>
        ✔ 75 voice minutes
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'free-trial'}>
        ✔ 25 image uploads
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'free-trial'}>
        ✔ Everything in Premium plan
      </PlanFeature>
      <PlanFeature selected={selectedPlan === 'free-trial'}>
        ✔ Video upload analysis
      </PlanFeature>
    </PlanFeatures>
  </PlanCard>

  </PlansContainer>

  <ButtonsContainer>
<Button
  onClick={handleUpgrade}
  disabled={loading || selectedPlan === 'free-trial'}   // 🔥 disable if it's coming soon
  variant={
    selectedPlan === 'basic'
      ? 'primary'
      : selectedPlan === 'premium'
      ? 'premium'
      : 'secondary'
  }
>
  {loading
    ? 'Processing...'
    : selectedPlan === 'free-trial'
    ? 'Coming Soon'   // 🔥 custom label
    : isAuthenticated
    ? `Start ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan`
    : `Sign Up - ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan`}
  {!loading &&
    selectedPlan !== 'free-trial' && (  // 🔥 hide icon for Coming Soon
      selectedPlan === 'premium' ? (
        <FiStar size={16} />
      ) : (
        <FiArrowRight size={16} />
      )
    )}
</Button>

  </ButtonsContainer>

  <CloseButton onClick={onClose}>
    <FiX size={20} />
  </CloseButton>
</UpgradeContainer>

  );
};

export default UpgradePrompt;
